import { useTranslation } from 'react-i18next';
import { Users, Building2, CreditCard, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useTenant } from '../providers/TenantProvider';

export function Dashboard() {
  const { t } = useTranslation();
  const { currentTenant } = useTenant();

  const stats = [
    {
      title: t('dashboard.totalMembers'),
      value: '12',
      icon: Users,
      description: '+2 this month',
    },
    {
      title: t('dashboard.activeUsers'),
      value: '8',
      icon: Activity,
      description: 'Last 24 hours',
    },
    {
      title: t('dashboard.currentPlan'),
      value: currentTenant?.plan || 'Free',
      icon: CreditCard,
      description: 'Upgrade available',
    },
    {
      title: t('nav.organization'),
      value: currentTenant?.name || '-',
      icon: Building2,
      description: currentTenant?.slug || '',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-muted-foreground">{t('dashboard.subtitle')}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.recentActivity')}</CardTitle>
            <CardDescription>Recent actions in your organization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-2 w-2 rounded-full bg-green-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">User invited</p>
                  <p className="text-xs text-muted-foreground">john@example.com - 2 hours ago</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-2 w-2 rounded-full bg-blue-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Settings updated</p>
                  <p className="text-xs text-muted-foreground">Security settings - 5 hours ago</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-2 w-2 rounded-full bg-yellow-500" />
                <div className="flex-1">
                  <p className="text-sm font-medium">User role changed</p>
                  <p className="text-xs text-muted-foreground">
                    jane@example.com to Admin - 1 day ago
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks for your organization</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <button className="w-full text-left px-4 py-2 rounded-lg hover:bg-accent transition-colors">
                <p className="text-sm font-medium">Invite team members</p>
                <p className="text-xs text-muted-foreground">Add new users to your organization</p>
              </button>
              <button className="w-full text-left px-4 py-2 rounded-lg hover:bg-accent transition-colors">
                <p className="text-sm font-medium">Configure settings</p>
                <p className="text-xs text-muted-foreground">Update organization preferences</p>
              </button>
              <button className="w-full text-left px-4 py-2 rounded-lg hover:bg-accent transition-colors">
                <p className="text-sm font-medium">View audit logs</p>
                <p className="text-xs text-muted-foreground">Review recent activity</p>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
